/** Shapes exchanged between the web app and the REST API. Timestamps are epoch ms. */

export interface User {
  id: string;
  username: string;
}

/** Sign-in methods the server accepts (GET /api/auth/methods). */
export interface AuthMethods {
  password: boolean;
  sso: boolean;
}

export interface FolderMeta {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
  deletedAt: number | null;
}

export interface DocumentMeta {
  id: string;
  folderId: string | null;
  title: string;
  createdAt: number;
  updatedAt: number;
  lastOpenedAt: number | null;
  deletedAt: number | null;
  /** Words at the last save; null until the document is first counted. */
  wordCount: number | null;
}

export interface Library {
  folders: FolderMeta[];
  documents: DocumentMeta[];
}

export interface BackupStatus {
  lastRunAt: number | null;
  lastSuccessAt: number | null;
  lastCommitSha: string | null;
  pushed: boolean | null;
  lastError: string | null;
}

/** Name of the per-user Hocuspocus document used only for library-changed broadcasts. */
export const libraryDocName = (userId: string) => `library:${userId}`;

/** Stateless message sent on the library document after any metadata change. */
export const LIBRARY_CHANGED = "library-changed";

export type SnapshotKind = "auto" | "manual" | "before-restore";

/** A saved version of a document (Phase 5). */
export interface SnapshotMeta {
  id: string;
  createdAt: number;
  wordCount: number;
  kind: SnapshotKind;
  /** Optional name given when saving a version by hand. */
  label: string | null;
  /** For "before-restore": the version that was restored. */
  sourceId: string | null;
}

export interface SnapshotPreview {
  snapshot: SnapshotMeta;
  /** ProseMirror JSON of the document at that version. */
  content: unknown;
}

/** Word targets; null = no goal. */
export interface WritingGoals {
  daily: number | null;
  weekly: number | null;
}

/** Length of one bucket of the words-written log. Small enough for any time zone's midnight. */
export const WORDS_BUCKET_MS = 15 * 60 * 1000;

/**
 * Net words written (added minus removed, across all documents) per 15-minute bucket,
 * oldest first. `at` is the bucket's start. Buckets with no change are left out.
 */
export interface WordsWritten {
  buckets: { at: number; words: number }[];
}
